import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import SecurityIcon from '@mui/icons-material/Security';
import { Alert, Box, Checkbox, FormControlLabel, TextField, Tooltip } from '@mui/material';
import { forwardRef, useContext, useImperativeHandle, useState } from 'react';
import { AiFillCalendar } from 'react-icons/ai';
import { GiLeg } from 'react-icons/gi';
import { TbSoccerField } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar, demarcaciones, lateralidades } from '../../utils/UtilesComun';
import SelectorClub from '../buscadores/SelectorClub';
import {
  ButtonApp,
  DialogApp,
  estiloBotonTablaJugador,
  IconButtonApp,
  SelectEleganteApp,
} from '../custom/CustomComponents';
import { IconSelectBoxApp } from '../custom/DedicatedComponents';

const CrearJugadorModal = forwardRef((props, ref) => {
  const { crearJugador, modificarJugador, idUsuario } = useContext(Context);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [jugadorEdicion, setJugadorEdicion] = useState(null);

  const [nombre, setNombre] = useState('');
  const [apellido1, setApellido1] = useState('');
  const [apellido2, setApellido2] = useState('');
  const [ano, setAno] = useState('');
  const [demarcacion, setDemarcacion] = useState('');
  const [lateralidad, setLateralidad] = useState('');
  const [clubActual, setClubActual] = useState('');
  const [anoCheck, setAnoCheck] = useState(true);
  const [seguirCreando, setSeguirCreando] = useState(false);
  const [mensajeExito, setMensajeExito] = useState('');

  const handleOpen = (jugadorParaEditar = null) => {
    if (jugadorParaEditar) {
      setJugadorEdicion(jugadorParaEditar);
      const [apellidosPart, nombrePart] = (jugadorParaEditar.nombre || '').split(', ');
      setNombre(capitalizar(nombrePart || ''));
      if (apellidosPart) {
        const parts = apellidosPart.split(' ');
        setApellido1(capitalizar(parts[0] || ''));
        setApellido2(capitalizar(parts.slice(1).join(' ') || ''));
      }
      setAno(jugadorParaEditar.ano || '');
      setDemarcacion(jugadorParaEditar.demarcacion || '');
      setLateralidad(jugadorParaEditar.lateralidad || '');
      setClubActual(jugadorParaEditar.clubActual || '');
      setAnoCheck(jugadorParaEditar.anoCheck !== false);
    } else {
      setJugadorEdicion(null);
      setNombre('');
      setApellido1('');
      setApellido2('');
      setAno('');
      setDemarcacion('');
      setLateralidad('');
      setClubActual('');
      setAnoCheck(true);
    }
    setMensajeExito('');
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    handleOpen,
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = async () => {
    const apellidos = `${apellido1.trim()} ${apellido2.trim()}`.trim().toUpperCase();
    const nombreFormateado = nombre.trim().toUpperCase();
    const nombreCompleto = `${apellidos}, ${nombreFormateado}`;

    const jugadorData = {
      nombre: nombreCompleto,
      ano: ano !== '' ? parseInt(ano, 10) : null,
      demarcacion,
      lateralidad,
      clubActual: clubActual.trim(),
      anoCheck,
      idUsuarioCreador: idUsuario,
    };

    if (jugadorEdicion) {
      const editado = await modificarJugador(jugadorEdicion.idJugador, jugadorData);
      if (editado) {
        handleClose();
        if (props.onUpdate) {
          props.onUpdate();
        }
      } else {
        alert('Error al modificar el jugador');
      }
    } else {
      const creado = await crearJugador(jugadorData);
      if (creado && creado.idJugador) {
        if (props.onUpdate) {
          props.onUpdate();
        }
        if (seguirCreando) {
          setNombre('');
          setApellido1('');
          setApellido2('');
          setAno('');
          setDemarcacion('');
          setLateralidad('');
          setClubActual('');
          setAnoCheck(true);
          setMensajeExito(`¡Jugador "${capitalizar(nombreCompleto)}" creado correctamente!`);
          setTimeout(() => setMensajeExito(''), 4000);
        } else {
          handleClose();
          navigate(`/jugador/${creado.idJugador}`);
        }
      } else {
        alert('Error al crear el jugador');
      }
    }
  };

  const currentYear = new Date().getFullYear();
  const isFormValid =
    nombre.trim() !== '' &&
    apellido1.trim() !== '' &&
    ano !== '' &&
    !isNaN(parseInt(ano, 10)) &&
    parseInt(ano, 10) >= currentYear - 50 &&
    parseInt(ano, 10) <= currentYear;

  const renderDialog = () => (
    <DialogApp
      open={open}
      onClose={handleClose}
      title={jugadorEdicion ? 'Editar Jugador' : 'Crear Jugador Manualmente'}
      maxWidth="xl"
      actions={
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', ml: 2, mr: 2, alignItems: 'center' }}
        >
          {!jugadorEdicion ? (
            <Tooltip title="Si marcas esta casilla, la ventana no se cerrará tras crear el jugador. Así podrás añadir varios jugadores seguidos rápidamente.">
              <FormControlLabel
                control={<Checkbox checked={seguirCreando} onChange={(e) => setSeguirCreando(e.target.checked)} />}
                label="Seguir creando jugadores"
              />
            </Tooltip>
          ) : (
            <Box />
          )}
          <ButtonApp onClick={handleSave} disabled={!isFormValid}>
            {jugadorEdicion ? 'Guardar Cambios' : 'Crear Jugador'}
          </ButtonApp>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1, mb: 0, alignItems: 'center' }}>
        {mensajeExito && (
          <Box sx={{ width: '100%' }}>
            <Alert severity="success">{mensajeExito}</Alert>
          </Box>
        )}
        {/* Nombre y Apellidos */}
        <IconSelectBoxApp icon={<PersonIcon />} showDivider={false}>
          <TextField
            variant="standard"
            placeholder="Nombre *"
            required
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            InputProps={{ sx: { fontSize: '0.85rem' } }}
            sx={{ minWidth: 90, mx: 1 }}
          />
        </IconSelectBoxApp>
        <IconSelectBoxApp showDivider={false}>
          <TextField
            variant="standard"
            placeholder="Apellido 1 *"
            required
            value={apellido1}
            onChange={(e) => setApellido1(e.target.value)}
            InputProps={{ sx: { fontSize: '0.85rem' } }}
            sx={{ minWidth: 90, mx: 1 }}
          />
        </IconSelectBoxApp>
        <IconSelectBoxApp>
          <TextField
            variant="standard"
            placeholder="Apellido 2"
            value={apellido2}
            onChange={(e) => setApellido2(e.target.value)}
            InputProps={{ sx: { fontSize: '0.85rem' } }}
            sx={{ minWidth: 90, mx: 1 }}
          />
        </IconSelectBoxApp>

        {/* Año */}
        <IconSelectBoxApp icon={<AiFillCalendar />}>
          <TextField
            variant="standard"
            placeholder="Año nacimiento *"
            type="number"
            required
            value={ano}
            onChange={(e) => setAno(e.target.value)}
            error={
              ano !== '' && ano.length >= 4 && (parseInt(ano, 10) < currentYear - 50 || parseInt(ano, 10) > currentYear)
            }
            helperText={
              ano !== '' && ano.length >= 4 && (parseInt(ano, 10) < currentYear - 50 || parseInt(ano, 10) > currentYear)
                ? `Entre ${currentYear - 50} y ${currentYear}`
                : ''
            }
            InputProps={{ sx: { fontSize: '0.85rem' } }}
            sx={{ minWidth: 50, width: 50, mx: 1 }}
          />
          <Box sx={{ ml: 1 }}>
            <Tooltip title="Año de nacimiento verificado">
              <Box>
                <IconButtonApp
                  onClick={() => setAnoCheck(!anoCheck)}
                  border
                  style={estiloBotonTablaJugador(anoCheck)}
                  sx={{ width: '30px', height: '30px', padding: 0 }}
                >
                  {anoCheck ? <CheckIcon fontSize="small" /> : <QuestionMarkIcon fontSize="small" />}
                </IconButtonApp>
              </Box>
            </Tooltip>
          </Box>
        </IconSelectBoxApp>

        {/* Club */}
        <IconSelectBoxApp icon={<SecurityIcon />}>
          <SelectorClub value={clubActual} onChange={setClubActual} />
        </IconSelectBoxApp>

        {/* Demarcacion */}
        <IconSelectBoxApp icon={<TbSoccerField style={{ marginRight: '8px' }} />}>
          <SelectEleganteApp
            value={demarcacion}
            onChange={(e) => setDemarcacion(e.target.value)}
            options={demarcaciones}
            displayEmpty
          />
        </IconSelectBoxApp>

        {/* Lateralidad */}
        <IconSelectBoxApp icon={<GiLeg style={{ marginRight: '8px' }} />} showDivider={false}>
          <SelectEleganteApp
            value={lateralidad}
            onChange={(e) => setLateralidad(e.target.value)}
            options={lateralidades}
            displayEmpty
          />
        </IconSelectBoxApp>
      </Box>
    </DialogApp>
  );

  return renderDialog();
});

CrearJugadorModal.displayName = 'CrearJugadorModal';

export default CrearJugadorModal;
